import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { Alert, Button, LinearProgress } from '@mui/material'
import { DataGrid, GridOverlay } from '@mui/x-data-grid'
import Loader from 'components/Loader'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useHistory } from 'react-router-dom'
import { apiConfig } from 'services'
import useAllRows from 'hooks/useAllRows'
import PageScrollContainer from './PageScrollContainer'

const emptySearchParams = {}
const emptyRows = []
const noop = () => null

export function ListLoadingStatus({ query }) {
  if (query.isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            onClick={() =>
              query.isFetchNextPageError
                ? query.fetchNextPage()
                : query.refetch()
            }
          >
            Retry
          </Button>
        }
      >
        Could not load all results. {query.list?.items.length || 0} rows loaded.
      </Alert>
    )
  }
  if (!query.isFetching && !query.hasNextPage) return null
  return (
    <Box role="status" py={1}>
      <Typography variant="body2">
        {query.list
          ? `Loading results… ${query.list.items.length} of ${query.list.total} loaded`
          : 'Loading results…'}
      </Typography>
      <LinearProgress />
    </Box>
  )
}

export function NoRowsOverlay({ text = 'No Results' }) {
  return (
    <GridOverlay>
      <Box flexDirection="row" display="flex" alignItems="center">
        <Box alignItems="center">
          <Typography variant="body1">{text}</Typography>
        </Box>
      </Box>
    </GridOverlay>
  )
}

export function LoadingOverlay({ text = 'Retrieving Data...' }) {
  return (
    <GridOverlay>
      <Loader title={text} message={null} />
    </GridOverlay>
  )
}

export function paramToSortModel(param) {
  const sort = param.startsWith('-') ? 'desc' : 'asc'
  const field = param.startsWith('-') ? param.substring(1) : param
  return { field: field, sort: sort }
}

export function sortModelToParam(sortModel) {
  const direction = sortModel.sort === 'asc' ? '' : '-'
  return `${direction}${sortModel.field}`
}

export function paramsToSearchObject(entries) {
  const search = {}
  for (const [key, value] of entries) {
    if (key !== 'page' && key !== 'ordering') search[key] = value
  }
  return search
}

export default function ServerSideDataGrid({
  apiCall,
  queryName,
  searchParams = emptySearchParams,
  noRowsText = null,
  loadingText = null,
  updateTotal = noop,
  pageSize = 10,
  allRows = false,
  pageScroll = false,
  refreshSignal,
  reserveViewportHeight = true,
  ...rest
}) {
  const queryClient = useQueryClient()
  const history = useHistory()
  const urlParams = new URLSearchParams(window.location.search)
  const tableRef = useRef()
  const [tableHeight, setTableHeight] = useState(400)
  const [totalCount, setTotalCount] = useState(0)
  const [sortModel, setSortModel] = useState(
    urlParams.has('ordering')
      ? [paramToSortModel(urlParams.get('ordering'))]
      : []
  )
  const [page, setPage] = useState(
    urlParams.has('page') ? Number(urlParams.get('page')) : 1
  )

  const handleSortModelChange = (model) => {
    setSortModel((currentModel) => {
      const currentSort = currentModel[0]
      const nextSort = model[0]

      if (
        currentSort?.field === nextSort?.field &&
        currentSort?.sort === nextSort?.sort
      ) {
        return currentModel
      }

      return model
    })
  }

  const handlePageChange = (page) => {
    setPage((currentPage) => {
      const nextPage = page + 1
      return currentPage === nextPage ? currentPage : nextPage
    })
  }

  const ordering = sortModel[0] ? sortModelToParam(sortModel[0]) : null
  const queryString = useMemo(() => {
    const params = new URLSearchParams()
    Object.keys(searchParams).forEach((key) => {
      const value = searchParams[key]
      if (value !== null && value !== undefined && value !== '') {
        params.set(key, value)
      }
    })
    return params.toString()
  }, [searchParams])

  const pageQuery = useQuery(
    [
      queryName,
      {
        currentApiInstitute: apiConfig.currentApiInstitute,
        sortModel,
        page,
        searchParams,
        size: pageSize
      }
    ],
    () => {
      return apiCall({
        ...searchParams,
        page: page - 1,
        size: pageSize,
        ordering: ordering
      })
    },
    { enabled: !allRows }
  )

  const allRowsQuery = useAllRows({
    queryName,
    apiCall,
    params: { ...searchParams, ordering },
    enabled: allRows,
    refreshSignal
  })
  const data = allRows ? allRowsQuery.list : pageQuery.data
  const { isLoading, isFetching } = allRows ? allRowsQuery : pageQuery

  useEffect(() => {
    if (!data) {
      return
    }

    const nextParams = new URLSearchParams()
    if (!allRows) nextParams.set('page', page)
    if (ordering) {
      nextParams.set('ordering', ordering)
    }
    if (queryString.length > 0) {
      const searchParams = new URLSearchParams(queryString)
      searchParams.forEach((value, key) => nextParams.set(key, value))
    }
    const nextSearch = nextParams.toString()

    if (window.location.search !== `?${nextSearch}`) {
      history.replace({
        pathname: window.location.pathname,
        search: nextSearch
      })
    }
    setTotalCount((currentTotal) =>
      currentTotal === data.total ? currentTotal : data.total
    )
    if (updateTotal) updateTotal(data.total)
  }, [data, history, ordering, page, queryString, updateTotal, allRows])

  useEffect(() => {
    // Invalidate the query when the page size changes
    if (!allRows) queryClient.invalidateQueries(queryName)
  }, [pageSize, queryClient, queryName, allRows])

  const handleTableHeight = () => {
    if (tableRef.current) {
      const nextHeight = Math.max(
        300,
        window.innerHeight - tableRef.current.offsetTop - 35
      )
      setTableHeight((currentHeight) =>
        currentHeight === nextHeight ? currentHeight : nextHeight
      )
    }
  }

  useEffect(() => {
    handleTableHeight()
  }, [data, tableRef?.current?.offsetTop, window.innerHeight])

  const rows = data?.items || emptyRows
  const rowsPerPageOptions = useMemo(() => [pageSize], [pageSize])
  const components = useMemo(
    () => ({
      LoadingOverlay: () =>
        loadingText ? (
          <LoadingOverlay text={loadingText} />
        ) : (
          <LoadingOverlay />
        ),
      NoRowsOverlay: () =>
        noRowsText ? <NoRowsOverlay text={noRowsText} /> : <NoRowsOverlay />
    }),
    [loadingText, noRowsText]
  )
  const scrollWithPage = allRows && pageScroll
  const Container = scrollWithPage ? PageScrollContainer : Box

  return (
    <>
      {allRows && <ListLoadingStatus query={allRowsQuery} />}
      <Container
        ref={scrollWithPage ? undefined : tableRef}
        height={
          scrollWithPage
            ? undefined
            : reserveViewportHeight
            ? tableHeight
            : 'auto'
        }
      >
        <DataGrid
          {...rest}
          rows={rows}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          page={allRows || page <= 0 ? 0 : page - 1}
          pageSize={pageSize}
          paginationMode="server"
          onPageChange={allRows ? undefined : handlePageChange}
          rowCount={totalCount}
          loading={allRows ? isLoading : isLoading || isFetching}
          rowsPerPageOptions={rowsPerPageOptions}
          hideFooterRowCount={true}
          hideFooter={allRows || totalCount <= 0}
          disableSelectionOnClick
          disableColumnMenu
          disableVirtualization={!allRows}
          density="comfortable"
          autoHeight={!scrollWithPage && (!allRows || !reserveViewportHeight)}
          components={components}
        />
      </Container>
    </>
  )
}
